// src/components/QuizManager.js
import React, { useEffect, useState } from "react";
import { getFirestore, collection, getDocs, doc, setDoc } from "firebase/firestore";

const QuizManager = () => {
  const db = getFirestore();
  const [quizzes, setQuizzes] = useState([]);
  const [status, setStatus] = useState("");
  const [uploading, setUploading] = useState(false);

  // Fetch all existing quizzes
  const fetchQuizzes = async () => {
    try {
      const quizCollection = await getDocs(collection(db, "quizzes"));
      const data = quizCollection.docs.map((doc) => ({
        slug: doc.id,
        ...doc.data(),
      }));
      setQuizzes(data);
    } catch (error) {
      console.error("Error fetching quizzes:", error);
    }
  };

  useEffect(() => {
    fetchQuizzes();
  }, []);

  // Handle file drop
  const handleDrop = async (event) => {
    event.preventDefault();
    setStatus("");
    setUploading(true);

    const file = event.dataTransfer.files[0];
    if (!file || file.type !== "application/json") {
      setStatus("❌ Please drop a valid .json file.");
      setUploading(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        await uploadMultipleQuizzes(jsonData);
      } catch (err) {
        console.error(err);
        setStatus("❌ Failed to parse or upload quizzes.");
      } finally {
        setUploading(false);
        fetchQuizzes();
      }
    };

    reader.readAsText(file);
  };

  // Upload multiple quizzes from JSON
  const uploadMultipleQuizzes = async (quizObject) => {
    if (typeof quizObject !== "object") {
      throw new Error("Invalid format. JSON must be an object of quizzes keyed by slug.");
    }

    const entries = Object.entries(quizObject);
    for (let [slug, data] of entries) {
      await setDoc(doc(db, "quizzes", slug), data);
    }

    setStatus(`✅ Uploaded ${entries.length} quizzes successfully.`);
  };

  // Export quizzes as downloadable .json
  const exportQuizzes = () => {
    const quizMap = {};
    quizzes.forEach((q) => {
      const { slug, ...data } = q;
      quizMap[slug] = data;
    });

    const blob = new Blob([JSON.stringify(quizMap, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quizzes_export.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.container}>
      <h2>🧠 Quiz Manager</h2>

      <div
        style={styles.dropZone}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        {uploading ? "Uploading..." : "🗂️ Drag & drop your .json file here"}
      </div>

      <p>{status}</p>

      <button style={styles.button} onClick={exportQuizzes}>
        📥 Download All Quizzes
      </button>

      <h3 style={{ marginTop: "30px" }}>📋 Existing Quizzes ({quizzes.length})</h3>
      <ul>
        {quizzes.map((q, i) => (
          <li key={i}>
            <strong>{q.slug}</strong>: {q.title || "Untitled"}
          </li>
        ))}
      </ul>
    </div>
  );
};

const styles = {
  container: {
    padding: "40px",
    maxWidth: "700px",
    margin: "0 auto",
    fontFamily: "Arial, sans-serif",
  },
  dropZone: {
    border: "2px dashed #4a90e2",
    padding: "30px",
    textAlign: "center",
    color: "#4a90e2",
    fontSize: "16px",
    borderRadius: "10px",
    marginBottom: "20px",
    cursor: "pointer",
  },
  button: {
    padding: "10px 20px",
    backgroundColor: "#4a90e2",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    marginTop: "10px",
  },
};

export default QuizManager;
